class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleBodyClick = this.handleBodyClick.bind(this);
    this.handleEscape = this.handleEscape.bind(this);

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = Array.from(
      new Set(document.querySelectorAll('#cart-icon-bubble, [data-cart-drawer-open]'))
    );
    if (!cartLinks.length) return;

    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-expanded', this.isOpen() ? 'true' : 'false');

      if (cartLink.dataset.cartDrawerBound === 'true') return;
      cartLink.dataset.cartDrawerBound = 'true';

      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggle(cartLink);
      });

      cartLink.addEventListener('keydown', (event) => {
        if (event.code && event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.toggle(cartLink);
        }
      });
    });
  }

  handleBodyClick(event) {
    if (!this.hasAttribute('open') && !this.classList.contains('active')) return;

    const drawerInner = this.querySelector('.drawer__inner');
    const clickedCartTrigger = event.target.closest(
      '#cart-icon-bubble, .header__icon--cart, [aria-controls="CartDrawer"], [data-cart-drawer-open]'
    );

    if (!drawerInner) return;
    if (drawerInner.contains(event.target)) return;
    if (clickedCartTrigger) return;

    this.close();
  }

  handleEscape(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  isOpen() {
    return this.hasAttribute('open') || this.classList.contains('active');
  }

  toggle(triggeredBy) {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open(triggeredBy);
    }
  }

  open(triggeredBy) {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }

    this.classList.remove('rp-closing');

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role') && typeof this.setSummaryAccessibility === 'function') {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.setAttribute('open', '');
    this.removeAttribute('aria-hidden');
    this.removeAttribute('inert');

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ?this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');

        if (containerToTrapFocusOn && focusElement && typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-drawer-open');
    document.querySelectorAll('#cart-icon-bubble, .header__icon--cart').forEach((cartTrigger) => {
      cartTrigger.setAttribute('aria-expanded', 'true');
    });

    document.addEventListener('click', this.handleBodyClick, true);
    document.addEventListener('keydown', this.handleEscape);
    document.dispatchEvent(new CustomEvent('retroparty:cart-drawer-open'));
  }

  close() {
    if (!this.isOpen() || this.classList.contains('rp-closing')) return;

    if (this.closeTimeout) clearTimeout(this.closeTimeout);

    this.classList.add('rp-closing');
    this.classList.remove('active');
    this.setAttribute('aria-hidden', 'true');
    this.setAttribute('inert', '');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.querySelectorAll('#cart-icon-bubble, .header__icon--cart').forEach((cartTrigger) => {
      cartTrigger.setAttribute('aria-expanded', 'false');
    });

    document.removeEventListener('click', this.handleBodyClick, true);
    document.removeEventListener('keydown', this.handleEscape);

    this.closeTimeout = setTimeout(() => {
      this.removeAttribute('open');
      this.classList.remove('rp-closing', 'animate');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('cart-drawer-open');
      document.dispatchEvent(new CustomEvent('retroparty:cart-drawer-close'));
      this.closeTimeout = null;
    }, 440);
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) {
      window.location = window.routes?.cart_url || '/cart';
      return;
    }

    this.classList.remove('is-empty');
    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) {
      inner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    let drawerWasRendered = false;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ?document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const html = parsedState.sections?.[section.id];
      if (!html) return;

      const newMarkup = this.getSectionInnerHTML(html, section.selector);
      if (!newMarkup.trim()) return;

      sectionElement.innerHTML = newMarkup;
      if (section.id === 'cart-drawer') drawerWasRendered = true;
    });

    this.syncCartDrawerTriggers();
    this.setHeaderCartIconAccessibility();
    window.RetroPartyCartSync?.syncHeaderCartSoon?.();
    window.RPFreeShipping?.updateFromCart?.(parsedState);

    if (!drawerWasRendered) {
      this.refreshCartDrawer().finally(() => this.openAfterRender(parsedState));
      return;
    }

    this.openAfterRender(parsedState);
  }

  openAfterRender(parsedState) {
    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      const addedNotice = this.querySelector('[data-rp-cart-added]');
      if (addedNotice && parsedState.title) {
        addedNotice.textContent = `${parsedState.title} ajouté au panier`;
        addedNotice.hidden = false;
      }
      this.open();
    });
  }

  refreshCartDrawer() {
    return fetch(`${window.routes?.cart_url || '/cart'}?section_id=cart-drawer`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart drawer refresh failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        const sectionElement = document.querySelector('#CartDrawer');
        if (!sectionElement) throw new Error('Cart drawer target missing');

        const newMarkup = this.getSectionInnerHTML(html, '#CartDrawer');
        if (!newMarkup.trim()) throw new Error('Cart drawer response is empty');
        sectionElement.innerHTML = newMarkup;
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return window.RetroPartyCartSync?.getSectionInnerHTML(html, selector) || '';
  }

  syncCartDrawerTriggers() {
    const sourceBadge = document.querySelector('#cart-icon-bubble .cart-count-bubble');

    document.querySelectorAll('[data-cart-drawer-open]').forEach((trigger) => {
      const currentBadge = trigger.querySelector('.cart-count-bubble');
      if (currentBadge) currentBadge.remove();
      if (sourceBadge) trigger.appendChild(sourceBadge.cloneNode(true));
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
